// Typsicherer API-Wrapper für alle Backend-Calls.
// Alle Requests gehen über fetchApi() – CSRF-Token wird automatisch mitgesendet.
// 401-Antworten leiten automatisch zum Login um.

import { gameState } from '../state/gameState.js';
import { navigate } from '../router.js';

// Antwortformat aus CLAUDE.md
export interface ApiSuccess<T> {
  data: T;
  meta: { timestamp: string };
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown>;
  };
}

export type ApiResult<T> = ApiSuccess<T> | ApiErrorBody;

function hasDataEnvelope<T>(body: unknown): body is ApiSuccess<T> {
  if (typeof body !== 'object' || body === null) return false;
  return 'data' in body;
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly details: Record<string, unknown>;
  public readonly status: number;

  constructor(code: string, message: string, details: Record<string, unknown>, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

const BASE_URL = '/api/v1';

// HTTP-Methoden die einen CSRF-Token brauchen (state-ändernde Requests)
const CSRF_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Führt den eigentlichen Request aus und gibt den geparsten Body unverändert zurück
 * (volle { data, meta }-Hülle bei Erfolg) — wirft ApiError bei Fehlerstatus/401.
 * Einzige Stelle mit der rohen fetch()-Logik; fetchApi() und api.getRaw() bauen beide darauf auf.
 */
async function requestJson<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiSuccess<T> | undefined> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);

  // Content-Type nur setzen wenn ein Body mitgesendet wird (Fastify parst sonst leeren Body als JSON → 400)
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');

  // CSRF-Token bei state-ändernden Requests aus gameState holen
  if (CSRF_METHODS.has(method)) {
    const csrfToken = gameState.getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    method,
    headers,
    // Session-Cookie automatisch mitsenden (httpOnly, sameSite: Strict)
    credentials: 'same-origin',
  });

  // 401: Session abgelaufen → Login
  if (response.status === 401) {
    gameState.clearPlayer();
    navigate('/login');
    throw new ApiError('UNAUTHORIZED', 'Sitzung abgelaufen. Bitte erneut einloggen.', {}, 401);
  }

  // Leere Antwort (z.B. 204 No Content)
  if (response.status === 204) {
    return undefined;
  }

  const body = (await response.json()) as ApiResult<T>;

  if (!response.ok) {
    const errBody = body as ApiErrorBody;
    throw new ApiError(
      errBody.error.code,
      errBody.error.message,
      errBody.error.details,
      response.status
    );
  }

  // Backwards compatibility: einige Endpunkte liefern bereits das Payload-Objekt direkt
  // statt { data, meta }. Kein `data`-Feld vorhanden → als reines Payload einwickeln, damit
  // der Rückgabetyp für Aufrufer einheitlich { data, meta } bleibt.
  if (hasDataEnvelope<T>(body)) {
    return body;
  }

  return { data: body as T, meta: { timestamp: new Date().toISOString() } };
}

async function fetchApi<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const result = await requestJson<T>(path, options);
  return result === undefined ? (undefined as T) : result.data;
}

// ─── Öffentliche API-Methoden ──────────────────────────────────────────────────

export const api = {
  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return fetchApi<T>(path, { method: 'GET', ...(signal ? { signal } : {}) });
  },

  /**
   * Wie get(), aber ohne automatisches Entpacken der { data, meta }-Hülle — für Endpunkte,
   * bei denen der Aufrufer echte `meta`-Felder braucht (z.B. Paginierung: total/page/pages),
   * die beim normalen get() sonst stillschweigend verloren gehen. `meta` bleibt bewusst
   * `Record<string, unknown>` — der Aufrufer kennt die konkrete Meta-Form des jeweiligen
   * Endpunkts und liest die Felder typisiert über sein eigenes Meta-Interface aus.
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T bewusst vom Aufrufer instanziert (wie bei get<T>()), damit result.data typisiert bleibt.
  async getRaw<T>(path: string, signal?: AbortSignal): Promise<{ data: T; meta: Record<string, unknown> }> {
    const result = await requestJson<T>(path, { method: 'GET', ...(signal ? { signal } : {}) });
    if (result === undefined) {
      throw new ApiError('EMPTY_RESPONSE', 'Leere Antwort ohne Inhalt erhalten.', {}, 204);
    }
    return result;
  },

  post<T>(path: string, body: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  delete<T>(path: string, body?: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },
};
